import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { ArrowUpRight, type LucideIcon } from "lucide-react";

export const spark = (seed: number) =>
  Array.from({ length: 14 }, (_, i) => ({
    x: i,
    y: 30 + Math.sin(i / 1.4 + seed) * 14 + Math.cos(i / 2.2 + seed) * 8 + seed * 3,
  }));

export type Tone = "neutral" | "success" | "warning" | "danger";

const strokeFor = (tone: Tone) =>
  tone === "success"
    ? "oklch(0.78 0.18 150)"
    : tone === "danger"
      ? "oklch(0.7 0.2 35)"
      : tone === "warning"
        ? "oklch(0.82 0.16 85)"
        : "oklch(0.7 0.02 240)";

const fillFor = (tone: Tone) =>
  tone === "success"
    ? "oklch(0.78 0.18 150 / 0.35)"
    : tone === "danger"
      ? "oklch(0.7 0.2 35 / 0.25)"
      : tone === "warning"
        ? "oklch(0.82 0.16 85 / 0.25)"
        : "oklch(0.7 0.02 240 / 0.25)";

const deltaClass = (tone: Tone) =>
  tone === "danger"
    ? "bg-accent/15 text-accent"
    : tone === "warning"
      ? "bg-[oklch(0.82_0.16_85)]/15 text-[oklch(0.85_0.15_85)]"
      : tone === "success"
        ? "bg-success/15 text-success"
        : "bg-white/10 text-muted-foreground";

export function HeaderKpi({
  icon: Icon,
  value,
  label,
  delta,
  tone = "neutral",
  seed = 1,
}: {
  icon: LucideIcon;
  value: string;
  label: string;
  delta: string;
  tone?: Tone;
  seed?: number;
}) {
  const gid = `kpi-${label.replace(/\s/g, "")}-${seed}`;
  return (
    <div className="card-surface relative overflow-hidden rounded-2xl p-5">
      <div className="flex items-start justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/5 text-muted-foreground">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${deltaClass(tone)}`}>
          {delta}
        </span>
      </div>
      <div className="mt-6 text-4xl font-light tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-3 h-10 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={spark(seed)} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={fillFor(tone)} />
                <stop offset="100%" stopColor="transparent" />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="y" stroke={strokeFor(tone)} strokeWidth={1.5} fill={`url(#${gid})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function SectionCard({
  title,
  scope,
  icon: Icon,
  cta = "Ouvrir",
  tone = "neutral",
  children,
}: {
  title: string;
  scope: string;
  icon: LucideIcon;
  cta?: string;
  tone?: Tone;
  children: React.ReactNode;
}) {
  const iconBg =
    tone === "success"
      ? "bg-success/15 text-success"
      : tone === "warning"
        ? "bg-[oklch(0.82_0.16_85)]/15 text-[oklch(0.85_0.15_85)]"
        : tone === "danger"
          ? "bg-accent/15 text-accent"
          : "bg-white/5 text-muted-foreground";
  return (
    <div className="card-surface flex h-full flex-col rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${iconBg}`}>
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium">{title}</div>
            <div className="truncate text-[11px] text-muted-foreground">{scope}</div>
          </div>
        </div>
        <button className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground">
          {cta} <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: Tone;
}) {
  const valueTone =
    tone === "danger"
      ? "text-accent"
      : tone === "warning"
        ? "text-[oklch(0.85_0.15_85)]"
        : tone === "success"
          ? "text-success"
          : "text-foreground";
  return (
    <div className="rounded-xl bg-white/[0.03] p-3 ring-1 ring-white/5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-light ${valueTone}`}>{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function RowItem({
  title,
  hint,
  onClick,
}: {
  title: string;
  hint?: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 rounded-lg bg-white/[0.02] px-3 py-2 text-left text-xs hover:bg-white/5"
    >
      <div className="min-w-0">
        <div className="truncate font-medium">{title}</div>
        {hint && <div className="truncate text-[10px] text-muted-foreground">{hint}</div>}
      </div>
      <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
    </button>
  );
}

export function CenterHeader({
  title,
  scope,
  onBack,
}: {
  title: string;
  scope: string;
  onBack: () => void;
}) {
  return (
    <div className="mb-1 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <div className="truncate text-xs text-muted-foreground">{scope}</div>
      </div>
      <button
        onClick={onBack}
        className="shrink-0 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        ← Cockpit
      </button>
    </div>
  );
}
